import { ReactElement, useState } from 'react';

import { type DemoChatMessage, MessageComposer, MessageList } from 'features/demo-chat';
import { extractErrorMessage } from 'shared/utils/extractErrorMessage';

import { Button } from '@/components/ui/button';

import { useUpdateProjectPromptSectionMutation } from '../api/promptSections.api';
import {
  SalesPromptRewriteMessage,
  SalesPromptRewriteTurn,
  useSendSalesPromptRewriteTurnMutation,
} from '../api/salesPromptRewrite.api';
import PromptDiffDialog from './PromptDiffDialog';

type EditedSection = SalesPromptRewriteTurn['sections'][number];

interface Props {
  masterProjectId: number;
  subProjectId: number | null;
  onUsePrompt: (prompt: string) => void;
}

const TAGS = /<(prompt|changelog)>[\s\S]*?<\/\1>|<section name="[^"]+"[^>]*>[\s\S]*?<\/section>/g;

const toChatMessage = (message: SalesPromptRewriteMessage, index: number): DemoChatMessage => ({
  id: index,
  clientId: 0,
  text:
    message.role === 'assistant'
      ? message.content.replace(TAGS, '').trim() || 'Te propongo una versión.'
      : message.content,
  sender: message.role === 'user' ? 'client' : 'agent',
  role: message.role,
  status: 'sent',
  fileName: null,
  fileUrl: null,
  createdAt: '',
});

const SalesPromptRewritePanel = ({ masterProjectId, subProjectId, onUsePrompt }: Props): ReactElement => {
  const [messages, setMessages] = useState<SalesPromptRewriteMessage[]>([]);
  const [lastTurn, setLastTurn] = useState<SalesPromptRewriteTurn | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [diffSection, setDiffSection] = useState<EditedSection | null>(null);
  const [sendTurn, { isLoading }] = useSendSalesPromptRewriteTurnMutation();
  const [updateSection, { isLoading: isPublishingSection }] = useUpdateProjectPromptSectionMutation();

  const send = async (history: SalesPromptRewriteMessage[]): Promise<void> => {
    const previous = messages;
    setMessages(history);
    setError(null);
    try {
      const turn = await sendTurn({ masterProjectId, subProjectId, messages: history }).unwrap();
      setMessages([...history, turn.message]);
      /* A turn that only asks or explains keeps the last proposal; sections accumulate, the newest edit wins. */
      setLastTurn((previous) => ({
        ...turn,
        prompt: turn.prompt ?? previous?.prompt ?? null,
        changelog: turn.prompt ? turn.changelog : previous?.changelog ?? turn.changelog,
        sections: [
          ...(previous?.sections ?? []).filter((kept) => !turn.sections.some((section) => section.id === kept.id)),
          ...turn.sections,
        ],
      }));
    } catch (err) {
      setMessages(previous);
      setError(extractErrorMessage(err, 'No se pudo generar la respuesta.'));
    }
  };

  const publishSection = async (section: EditedSection): Promise<void> => {
    try {
      await updateSection({
        id: section.id,
        projectId: masterProjectId,
        name: section.name,
        displayName: section.displayName,
        permission: section.permission,
        value: section.value,
      }).unwrap();
      setDiffSection(null);
      setLastTurn((turn) => turn && { ...turn, sections: turn.sections.filter((s) => s.id !== section.id) });
    } catch (err) {
      setError(extractErrorMessage(err, 'No se pudo publicar la sección.'));
    }
  };

  return (
    <div className="flex h-full flex-col">
      <header className="border-b border-border-subtle px-4 py-3">
        <p className="font-semibold text-text-default">Adaptar al orientador</p>
        <p className="text-xs text-text-muted">Reescribe el prompt de producción con la skill de ventas multiagente.</p>
      </header>

      {messages.length === 0 && !isLoading ? (
        <div className="flex flex-1 items-center justify-center">
          <Button type="button" onClick={() => send([])}>
            Analizar prompt
          </Button>
        </div>
      ) : (
        <MessageList messages={messages.map(toChatMessage)} isLoading={isLoading} />
      )}

      {isLoading && <p className="px-4 py-2 text-xs text-text-muted">Pensando… puede tardar un par de minutos.</p>}
      {error && <p className="px-4 py-2 text-xs text-destructive">{error}</p>}

      {lastTurn && (lastTurn.prompt || lastTurn.sections.length > 0) && !isLoading && (
        <div className="space-y-2 border-t border-border-subtle px-4 py-3">
          {lastTurn.changelog && (
            <p className="max-h-32 overflow-y-auto whitespace-pre-wrap text-xs text-text-muted">{lastTurn.changelog}</p>
          )}
          {lastTurn.prompt && (
            <Button type="button" size="sm" onClick={() => onUsePrompt(lastTurn.prompt ?? '')}>
              Usar esta versión
            </Button>
          )}
          {lastTurn.sections.map((section) => (
            <Button key={section.id} type="button" size="sm" variant="outline" onClick={() => setDiffSection(section)}>
              Revisar sección {section.displayName}
            </Button>
          ))}
        </div>
      )}

      <MessageComposer
        maxFiles={0}
        disabled={isLoading || messages.length === 0}
        onSend={(text) => send([...messages, { role: 'user', content: text }])}
      />

      {diffSection && (
        <PromptDiffDialog
          open
          onOpenChange={(open) => !open && setDiffSection(null)}
          title={`Sección ${diffSection.displayName}`}
          subtitle={`<SECTION:${diffSection.name}> · compartida con otros subproyectos que la usen`}
          oldContent={diffSection.currentValue}
          newContent={diffSection.value}
          cancelLabel="Cancelar"
          primaryAction={{
            label: 'Publicar sección',
            disabled: isPublishingSection,
            onClick: () => publishSection(diffSection),
          }}
        />
      )}
    </div>
  );
};

export default SalesPromptRewritePanel;
